import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, Lock, ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";
import ForecastLayout from "../../components/forecast/ForecastLayout";
import DataGrid from "../../components/datatables/DataGrid";
import { OriginBadge } from "../../lib/origin.jsx";
import { getSource } from "../../lib/forecastApi";
import { cn } from "../../lib/utils";

const SIZE = 50;

function useSource(id, params) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const key = JSON.stringify(params);
  useEffect(() => {
    let alive = true;
    setState(s => ({ ...s, loading: true, error: null }));
    const t = setTimeout(() => getSource(id, JSON.parse(key))
      .then(data => alive && setState({ data, error: null, loading: false }))
      .catch(e => alive && setState(s => ({ ...s, error: e.message, loading: false }))), 200);
    return () => { alive = false; clearTimeout(t); };
  }, [id, key]);
  return state;
}

function Toolbar({ d, scope, setScope, q, setQ, from, setFrom, to, setTo }) {
  const input = "h-9 bg-surface border border-border rounded-sm px-2.5 text-[12.5px] focus:outline-none focus:border-primary";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="fc-source-toolbar">
      {d.filtered && (
        <div className="inline-flex border border-border rounded-sm overflow-hidden text-[12px]" data-testid="fc-source-scope">
          {[["line", "Rows behind this number"], ["all", "Full table"]].map(([k, l]) => (
            <button key={k} data-testid={`fc-source-scope-${k}`} onClick={() => setScope(k)}
                    className={cn("h-9 px-3 transition-colors", scope === k ? "bg-primary/15 text-primary" : "text-muted hover:text-fg")}>{l}</button>
          ))}
        </div>
      )}
      <label className="relative flex-1 min-w-[180px] max-w-sm">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
        <input data-testid="fc-source-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search every field" className={cn(input, "w-full pl-8")} />
      </label>
      {d.date && (
        <div className="inline-flex items-center gap-1.5 text-[11.5px] text-muted" data-testid="fc-source-dates">
          <CalendarRange size={13} />
          <input type="date" data-testid="fc-source-date-from" value={from} onChange={e => setFrom(e.target.value)} className={input} />
          <span>–</span>
          <input type="date" data-testid="fc-source-date-to" value={to} onChange={e => setTo(e.target.value)} className={input} />
          <span className="hidden md:inline font-mono-tabular">on {d.date}</span>
        </div>
      )}
    </div>
  );
}

// Read-only view of the full source table behind a Forecast / Prediction line: search, sort, date range, paging.
export default function SourceView() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const id = sp.get("id") || "";
  const [scope, setScope] = useState("line");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const [page, setPage] = useState(1);
  const params = useMemo(() => ({ scope, q, page, size: SIZE, dir: sort.dir, ...(sort.key ? { sort: sort.key } : {}),
    ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) }), [scope, q, page, sort, from, to]);
  useEffect(() => { setPage(1); }, [scope, q, sort, from, to]);
  const { data: d, error, loading } = useSource(id, params);
  const pages = d ? Math.max(1, Math.ceil(d.total / SIZE)) : 1;
  const onSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <ForecastLayout eyebrow="Forecast / Prediction Models · Source data" title={d ? d.title : "Source data"}
      description={d ? d.about : "Read-only view of the rows behind a number."}
      action={<button onClick={() => (window.history.length > 1 ? nav(-1) : nav("/forecast/overview"))} data-testid="fc-source-back"
                      className="inline-flex items-center gap-1.5 h-9 px-3 border border-border rounded-sm text-[12.5px] hover:border-primary hover:text-primary shrink-0"><ArrowLeft size={13} />Back</button>}>
      {error && !d && <div className="tile p-6 text-sm text-critical" data-testid="fc-source-error">{error}</div>}
      {!d && !error && <div className="tile p-6 text-sm text-muted animate-pulse" data-testid="fc-source-loading">Loading source rows…</div>}
      {d && (
        <div className="tile" data-testid="fc-source">
          <div className="px-4 py-3 border-b border-border space-y-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px]">
              <span className="text-muted">Behind <span className="text-fg font-medium" data-testid="fc-source-line">{d.line.label} · {d.line.display}</span></span>
              <OriginBadge origin={d.origin} source={`${d.title} · ${d.origin === "synthetic" ? "sample data" : "platform data"}`} testid="fc-source-origin" />
              <span className="inline-flex items-center gap-1 text-muted"><Lock size={11} />Read-only · no personal details in this table</span>
            </div>
            <Toolbar d={d} scope={scope} setScope={setScope} q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} />
          </div>
          {error && <div className="px-4 py-2 text-[12px] text-critical" data-testid="fc-source-error">{error}</div>}
          <DataGrid columns={d.columns} rows={d.rows} sort={sort.key} dir={sort.dir} masked={false} onSort={onSort} onOpen={() => {}} loading={loading} />
          {!d.rows.length && <div className="p-6 text-sm text-muted" data-testid="fc-source-empty">No rows match these filters.</div>}
          <div className="px-4 py-3 flex items-center justify-between text-[12px] text-muted font-mono-tabular">
            <span data-testid="fc-source-count">{d.total.toLocaleString()} rows{d.scope === "line" && d.filtered ? " behind this number" : ""} · {d.rows_total.toLocaleString()} in table</span>
            <span className="inline-flex items-center gap-2">
              <button data-testid="fc-source-prev" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm disabled:opacity-40"><ChevronLeft size={14} /></button>
              <span data-testid="fc-source-page">{page} / {pages}</span>
              <button data-testid="fc-source-next" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm disabled:opacity-40"><ChevronRight size={14} /></button>
            </span>
          </div>
        </div>
      )}
    </ForecastLayout>
  );
}
